import TableHeader from "@/components/common/table/header";
import { TableLeadingCell } from "@/components/ui/table-leading-cell";
import { filterTypesEnum, type FilterOption } from "@/types/common/filters";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  TableData,
  useToast,
} from "@elaniintc/goes-design-system";
import TablePaginantion from "@/components/common/table/pagination";
import {
  createColumnHelper,
  type ColumnDef,
  type SortingState, // Temporalmente comentado para la prueba
} from "@tanstack/react-table";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router";
import type { ApprovalGroupType } from "@/types/entities/approval-groups";
import {
  useGetApprovalGroups,
  useDeleteApprovalGroup,
  useRestoreApprovalGroup,
} from "@/queries/approval-groups";
import { FiltersModal } from "@/components/ui/filters/FiltersModal";
import { parseAsInteger, parseAsString, useQueryState } from "nuqs";
import { useDebounceValue } from "usehooks-ts";
import { cn } from "@/utils/theme";
import type { AxiosError } from "axios";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import DeactivateModal from "./deactivate-modal";

const defaultFilters: (val: {
  status?: string;
  updated_at?: string;
}) => FilterOption[] = ({
  status = "active",
  updated_at = "",
}: {
  status?: string;
  updated_at?: string;
}) => [
  {
    id: 1,
    field: "status",
    type: filterTypesEnum.status,
    label: "Estado",
    isUpdated: true,
    result: status,
    status: [
      {
        field: "active",
        label: "Activos",
        value: status.includes("active"),
      },
      {
        field: "only",
        label: "Inactivos",
        value: status.includes("only"),
      },
    ],
  },
  {
    id: 2,
    field: "updated_at",
    type: filterTypesEnum.dates,
    label: "Última actualización",
    shouldDisabledFutureDates: true,
    isUpdated: updated_at !== "",
    result: updated_at,
  },
];

const columnHelper = createColumnHelper<ApprovalGroupType>();

const columnsDef = ({
  canUpdate,
  canDelete,
  onCellButtonClick,
  onDelete,
  onRestore,
}: {
  canDelete: boolean;
  canUpdate: boolean;
  onCellButtonClick: (group: ApprovalGroupType) => void;
  onDelete: (id: string) => void;
  onRestore: (id: string) => void;
}) => {
  return [
    columnHelper.accessor("name", {
      id: "name",
      header: "Nombre del grupo",
      meta: {
        className: "w-[350px] min-w-[350px] max-w-[350px]",
        cellClassName: "group", // important to enable hover state on button
      },
      cell: (info) => {
        const { name, description } = info.row.original;
        return (
          <TableLeadingCell
            className="text-size-body"
            title={name}
            subtitle={description}
            showButton={true}
            onButtonClick={() => onCellButtonClick(info.row.original)}
          />
        );
      },
    }),
    columnHelper.accessor("members_count", {
      id: "usersCount",
      header: "Número de Usuarios",
      enableSorting: false,
      meta: {
        className: "text-size-body",
      },
      cell: (info) => info.getValue()?.toString() + " Usuarios Activos",
    }),
    columnHelper.accessor("updated_at", {
      id: "updated_at",
      header: "Última Actualización",
      meta: {
        className: "text-size-body hidden md:table-cell",
      },
      cell: (info) => {
        const date = info.getValue();
        return date ? dayjs(date).format("MMM DD, YYYY | HH:mm A") : "-";
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      meta: {
        className: "w-[56px]",
      },
      cell: (info) => {
        const id = info.row.original.id;
        return (
          <div className="w-full flex justify-end">
            <GroupOptions
              id={id}
              onDelete={onDelete}
              onRestore={onRestore}
              canUpdate={canUpdate}
              isActive={info.row.original.deleted_at ? false : true}
              canDelete={info.row.original.canDeactivate! && canDelete}
            />
          </div>
        );
      },
    }),
  ];
};

const GroupOptions = ({
  id,
  canUpdate,
  isActive,
  canDelete,
  onDelete,
  onRestore,
}: {
  id: string;
  canUpdate: boolean;
  isActive: boolean;
  canDelete: boolean;
  onDelete: (id: string) => void;
  onRestore: (id: string) => void;
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="xs" className="size-8">
          <Icon
            type="far"
            name="ellipsis-vertical"
            className="text-current text-sm"
          />
          <span className="sr-only">Acciones para el grupo</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[222px] min-w-56 rounded-lg border-neutral-silent text-body font-normal p-2"
        side="bottom"
        align="end"
        sideOffset={4}
      >
        <DropdownMenuGroup>
          <DropdownMenuItem
            className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
            asChild
          >
            <NavLink to={`/usuarios/grupos/${id}`}>Ver Grupo</NavLink>
          </DropdownMenuItem>
          {canDelete && isActive && (
            <DropdownMenuItem
              className={cn(
                "hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer",
                !canDelete && "pointer-events-none text-on-disabled",
              )}
              onClick={() => canDelete && onDelete(id)}
            >
              Inactivar Grupo
            </DropdownMenuItem>
          )}
          {canUpdate && !isActive && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              onClick={() => onRestore(id)}
            >
              Activar Grupo
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ApprovalGroupsList = () => {
  const { showToast } = useToast();
  const [sorting, setSorting] = useState<SortingState>([]);
  const navigate = useNavigate();
  const { hasPermission } = useAuthorization();
  const canCreate = hasPermission(Permissions["approval-groups"].create);
  const canUpdate = hasPermission(Permissions["approval-groups"].update);
  const canDelete = hasPermission(Permissions["approval-groups"].delete);
  const [modalState, setModalState] = useState<
    "idle" | "filters" | "activate" | "deactivate"
  >("idle");
  const [query, setQuery] = useQueryState("search", parseAsString);
  const [searchInput, setSearchInput] = useState<string | null>(query || "");
  const [page, setPage] = useQueryState("page", parseAsInteger);
  const [deferredQuery] = useDebounceValue(searchInput || "", 500);
  const [updated_at, setUpdatedAt] = useQueryState(
    "updated_at",
    parseAsString.withDefault(""),
  );
  const [order_by, setOrderBy] = useQueryState(
    "order_by",
    parseAsString.withDefault("!updated_at"),
  );
  const [status, setStatus] = useQueryState(
    "status",
    parseAsString.withDefault("active"),
  );
  const [formatFilters, setFormatFilters] = useState<FilterOption[]>(
    defaultFilters({
      status,
      updated_at,
    }),
  );

  // API call to get approval groups
  const { data, isFetching } = useGetApprovalGroups({
    page: page || 1,
    per_page: 20,
    search: deferredQuery || undefined,
    order_by: order_by || "name",
    trashed: status || "active",
    type: "approval",
    updated_at: updated_at || undefined,
  });

  const [selectedGroupToAction, setSelectedGroupToAction] = useState<
    string | null
  >(null);
  const { mutateAsync: mutateDeleteGroup, isPending: isLoadingDeactivate } =
    useDeleteApprovalGroup(selectedGroupToAction!);
  const { mutateAsync: mutateRestoreGroup, isPending: isLoadingActivate } =
    useRestoreApprovalGroup(selectedGroupToAction!);

  const onDeleteGroup = useCallback(async () => {
    try {
      await mutateDeleteGroup();
      setSelectedGroupToAction(null);
      showToast({
        title: "Grupo inactivado",
        description: "El grupo ha sido inactivado exitosamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    } catch (e) {
      setSelectedGroupToAction(null);
      const error = e as AxiosError;
      const isConflict = error?.response?.status === 409;
      showToast({
        title: "Error al inactivar grupo",
        description: isConflict
          ? "El grupo está en uso y no puede ser inactivado."
          : "No se pudo inactivar el grupo. Inténtalo de nuevo.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
      console.error("Error deleting group:", error);
    }
  }, [mutateDeleteGroup, showToast]);

  const onRestoreGroup = useCallback(async () => {
    try {
      await mutateRestoreGroup();
      setSelectedGroupToAction(null);
      showToast({
        title: "Grupo activado",
        description: "El grupo ha sido activado exitosamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    } catch (error) {
      setSelectedGroupToAction(null);
      showToast({
        title: "Error al activar grupo",
        description: "No se pudo activar el grupo. Inténtalo de nuevo.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
      console.error("Error restoring group:", error);
    }
  }, [mutateRestoreGroup, showToast]);

  const columns = useMemo(
    () =>
      columnsDef({
        canUpdate,
        canDelete,
        onCellButtonClick: (group) => {
          navigate(`/usuarios/grupos/${group.id}`);
        },
        onDelete: (id) => {
          setSelectedGroupToAction(id);
          setModalState("deactivate");
        },
        onRestore: (id) => {
          setSelectedGroupToAction(id);
          setModalState("activate");
        },
      }),
    [canUpdate, canDelete, navigate],
  );

  const onApplyFilters = (newFilters: FilterOption[]): FilterOption[] => {
    newFilters.forEach((f) => {
      if (f.isUpdated) {
        if (f.field === "status") {
          setStatus(f.result);
        } else if (f.field === "updated_at") {
          setUpdatedAt(f.result);
        }
        setPage(1);
      }
    });

    // Set filter to updated false if result is empty
    const updatedFilters = newFilters.map((f) => {
      if (f.result === "") {
        return {
          ...f,
          isUpdated: false,
          result: "",
        };
      }
      return f;
    });
    return updatedFilters;
  };

  const onCloseFilters = (type: string) => {
    if (type !== "clean") {
      setModalState("idle");
      return;
    }
    setFormatFilters(
      defaultFilters({
        status: "active",
        updated_at: "",
      }),
    );
    setStatus(null);
    setUpdatedAt(null);
    setModalState("idle");
  };

  useEffect(() => {
    setQuery(deferredQuery || null);
    setPage(1);
  }, [deferredQuery, setQuery, setPage]);

  return (
    <div className="h-full flex flex-col gap-3">
      <p className="font-normal text-body leading-3xl">
        Administra los grupos de asignación disponibles para los usuarios de
        SDT.
      </p>
      <div className="h-full min-h-[500px] relative">
        <div className="h-full flex flex-col bg-neutral-object border-neutral border-[0.5px] rounded-xl shadow-neutral-md overflow-hidden">
          <TableHeader
            title="Grupos disponibles"
            searchPlaceholder="Buscar..."
            search={searchInput}
            isLoading={isFetching}
            setSearch={setSearchInput}
            canCreate={canCreate}
            createLabel="Crear Grupo"
            onCreate={() => navigate("/usuarios/grupos/new")}
            onFilter={() => {
              setModalState("filters");
            }}
            filtersApplied={formatFilters.filter((f) => f.isUpdated).length}
          />
          <TableData
            wrapperClassName="min-h-0 flex-1 overflow-auto h-full relative"
            headerClassName="sticky top-0 bg-neutral-object z-20"
            columns={columns as ColumnDef<ApprovalGroupType>[]}
            data={data?.data || []}
            sorting={sorting}
            onSortingChange={(val) => {
              setSorting(val);
              if (val.length === 0) {
                setOrderBy("!updated_at");
                return;
              }
              setOrderBy(
                val.length > 0
                  ? `${val[0]?.desc ? "!" : ""}${val[0].id}`
                  : "!updated_at",
              );
            }}
            isFirstLoading={!data}
            stripped
          />
          <div className="p-3">
            <TablePaginantion
              totalPages={data?.meta?.last_page || 0}
              totalItems={data?.meta?.total || 0}
              pageSize={data?.data.length || 0}
              currentPage={data?.meta?.current_page || 1}
              hasNext={
                (data?.meta?.current_page || 0) < (data?.meta?.last_page || 0)
              }
              hasPrevious={(data?.meta?.current_page || 0) > 1}
              onPageChange={(page) => {
                setPage(page);
              }}
              onNext={() => {
                if (data?.meta?.current_page && data?.meta?.last_page) {
                  if (data.meta?.current_page < data.meta?.last_page) {
                    setPage(data.meta?.current_page + 1);
                  }
                }
              }}
              onPrevious={() => {
                if (data?.meta?.current_page && data?.meta?.last_page) {
                  if (data.meta?.current_page > 1) {
                    setPage(data.meta?.current_page - 1);
                  }
                }
              }}
            />
          </div>
          {modalState === "filters" && (
            <FiltersModal
              isOpen={modalState === "filters"}
              onToggle={onCloseFilters}
              hasCurrentFilters={formatFilters.some(
                (filter) => filter.isUpdated,
              )}
              items={formatFilters}
              onApply={(newFilters) => {
                const filters = onApplyFilters(newFilters);
                setFormatFilters(filters);
                setModalState("idle");
              }}
            />
          )}
          {modalState === "activate" && (
            <DeactivateModal
              isOpen={modalState === "activate"}
              onClose={() => setModalState("idle")}
              onAccept={() => {
                setModalState("idle");
                onRestoreGroup();
              }}
              isActive={false}
              isLoading={isLoadingActivate || isLoadingDeactivate}
            />
          )}
          {modalState === "deactivate" && (
            <DeactivateModal
              isOpen={modalState === "deactivate"}
              onClose={() => setModalState("idle")}
              onAccept={() => {
                setModalState("idle");
                onDeleteGroup();
              }}
              isActive={true}
              isLoading={isLoadingDeactivate || isLoadingActivate}
            />
          )}
        </div>
      </div>
    </div>
  );
};
